import { Route, RedirectFunction, Router } from '@angular/router';
import { inject } from '@angular/core';
import { authGuard } from './core/auth/auth.guard';
import { seoRouteResolver } from './core/seo/seo-route.resolver';
import { isSensitiveRedirectKey } from '../seo/public-url-policy';

const eventAliasRedirect: RedirectFunction = ({
  params,
  queryParams,
  fragment,
}) =>
  inject(Router).createUrlTree(['/evento', params['slug']], {
    queryParams: Object.fromEntries(
      Object.entries(queryParams).filter(
        ([key]) => !isSensitiveRedirectKey(key)
      )
    ),
    fragment: fragment ?? undefined,
  });

const routes: Route[] = [
  {
    path: '',
    loadComponent: () =>
      import('./features/home/home-page.component').then(
        (m) => m.HomePageComponent
      ),
  },
  {
    path: 'politica-de-privacidade',
    loadComponent: () =>
      import('./features/legal/legal-page.component').then(
        (m) => m.LegalPageComponent
      ),
    data: { document: 'privacy' },
  },
  {
    path: 'termos-de-uso',
    loadComponent: () =>
      import('./features/legal/legal-page.component').then(
        (m) => m.LegalPageComponent
      ),
    data: { document: 'terms' },
  },
  {
    path: 'preferencias-de-cookies',
    loadComponent: () =>
      import('./features/legal/cookie-preferences-page.component').then(
        (m) => m.CookiePreferencesPageComponent
      ),
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login-page.component').then(
        (m) => m.LoginPageComponent
      ),
  },
  {
    path: 'register',
    loadComponent: () =>
      import('./features/auth/register-page.component').then(
        (m) => m.RegisterPageComponent
      ),
  },
  {
    path: 'invite',
    loadComponent: () =>
      import('./features/invites/invite-accept-page.component').then(
        (m) => m.InviteAcceptPageComponent
      ),
  },
  {
    path: 'e/:slug',
    redirectTo: eventAliasRedirect,
    pathMatch: 'full',
  },
  {
    path: 'event/:slug',
    redirectTo: eventAliasRedirect,
    pathMatch: 'full',
  },
  {
    path: 'evento/:slug',
    loadComponent: () =>
      import(
        './features/events/event-landing-page/event-landing-page.component'
      ).then((m) => m.EventLandingPageComponent),
  },
  {
    path: 'eventos',
    loadComponent: () =>
      import(
        './features/events/events-list-page/events-list-page.component'
      ).then((m) => m.EventsListPageComponent),
  },
  {
    path: 'eventos/:cidade',
    loadComponent: () =>
      import(
        './features/events/events-list-page/events-list-page.component'
      ).then((m) => m.EventsListPageComponent),
  },
  {
    path: 'eventos/:cidade/:categoria',
    loadComponent: () =>
      import(
        './features/events/events-list-page/events-list-page.component'
      ).then((m) => m.EventsListPageComponent),
  },
  {
    path: 'checkout/start',
    loadComponent: () =>
      import('./features/checkout/checkout-start.component').then(
        (m) => m.CheckoutStartComponent
      ),
  },
  {
    path: 'r/:token',
    loadComponent: () =>
      import('./features/recovery/recovery-redirect-page.component').then(
        (m) => m.RecoveryRedirectPageComponent
      ),
  },
  {
    path: 'checkout/confirm/:orderId',
    loadComponent: () =>
      import('./features/checkout/checkout-confirm-page.component').then(
        (m) => m.CheckoutConfirmPageComponent
      ),
  },
  {
    path: 'online-access',
    loadComponent: () =>
      import('./features/online-access/online-access-list.component').then(
        (m) => m.OnlineAccessListComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: 'live',
    loadComponent: () =>
      import(
        './features/events/event-live-page/event-live-page.component'
      ).then((m) => m.EventLivePageComponent),
  },
  {
    path: 'tickets/:publicToken',
    loadComponent: () =>
      import('./features/tickets/public-ticket-page.component').then(
        (m) => m.PublicTicketPageComponent
      ),
  },
  {
    path: 'baixar-ingressos/confirmar',
    data: { recoveryConfirmation: true },
    loadComponent: () =>
      import('./features/tickets/ticket-recovery-page.component').then(
        (m) => m.TicketRecoveryPageComponent
      ),
  },
  {
    path: 'baixar-ingressos',
    loadComponent: () =>
      import('./features/tickets/ticket-recovery-page.component').then(
        (m) => m.TicketRecoveryPageComponent
      ),
  },
  {
    path: 'account',
    loadComponent: () =>
      import('./features/account/account-page.component').then(
        (m) => m.AccountPageComponent
      ),
  },
  {
    path: 'perguntas-frequentes',
    loadComponent: () =>
      import('./features/institutional/faq-page.component').then(
        (m) => m.FaqPageComponent
      ),
  },
  {
    path: 'sobre',
    loadComponent: () =>
      import('./features/institutional/about-page.component').then(
        (m) => m.AboutPageComponent
      ),
  },
  {
    path: 'contato',
    loadComponent: () =>
      import('./features/institutional/contact-page.component').then(
        (m) => m.ContactPageComponent
      ),
  },
  {
    path: 'ajuda',
    loadComponent: () =>
      import('./features/institutional/help-page.component').then(
        (m) => m.HelpPageComponent
      ),
  },
  {
    path: 'organizadores',
    loadComponent: () =>
      import('./features/institutional/organizers-page.component').then(
        (m) => m.OrganizersPageComponent
      ),
  },
  {
    path: '**',
    loadComponent: () =>
      import('./features/errors/not-found-page.component').then(
        (m) => m.NotFoundPageComponent
      ),
  },
];

export const appRoutes: Route[] = routes.map((route) =>
  route.redirectTo !== undefined
    ? route
    : {
        ...route,
        resolve: { ...route.resolve, seo: seoRouteResolver },
        // Keep the existing auth guard navigation policy on guarded routes.
        runGuardsAndResolvers: route.canActivate?.length
          ? route.runGuardsAndResolvers
          : 'paramsOrQueryParamsChange',
      }
);
